import styles from "./styles.module.scss";
import { TableList } from "./TableList";
import { useEffect, useMemo, useState } from "react";
import { ColumnsModal } from "./ColumnsModal";
import { useNavigate } from "react-router-dom";
import { RelativeComponentLoader } from "../../components/loader";
import { Card, CardContent, CardHeader, Sheet, SheetContent, Typography } from "@altimateai/lego";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GET_DBT_MODELS, GET_DBT_MODELS_FILTERS } from "./constants";
import { getDbtModelByRk, getDbtModelsFilters, getDbtModels } from "../../helpers/apis";
import { NoDbtModels } from "./NoDbtModels";
import { DbtModel } from "@/types/dbtModels";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { Stack, Tooltip } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { IconInfoCircle } from "@tabler/icons-react";
import { useBreadcrumb } from "@/components/breadcrumbs";
import type { IntegrationFilterOption, FilterOption, GetDbtModelsParams } from "@/types/dbtModels";
import type {
  FilterOptionWithValue,
  ComputedFilters,
  DbtModelFilterKey,
  DbtModelFilterAccumulator,
} from "./types";
import DbtModelsNewFilters from "./DbtModelsNewFilters";
import { disambiguateProjects } from "./filterUtils";

const FILTER_BE_KEYS: Record<string, DbtModelFilterKey> = {
  dbt_core_integrations: "dbt_core_integration_ids",
  dbt_cloud_integrations: "dbt_core_integration_ids",
  environments: "dbt_core_integration_environment_ids",
  projects: "project_rk",
  resource_types: "resource_type",
  materializations: "materialization",
  databases: "database",
  schemas: "model_schema",
  is_stale: "is_stale",
};

// URL param key for both dbt_core_integrations and dbt_cloud_integrations
const FILTER_URL_KEY: Record<string, string> = {
  dbt_core_integrations: "dbt_integrations",
  dbt_cloud_integrations: "dbt_integrations",
};

const FILTER_KEYS = [
  "dbt_core_integrations",
  "dbt_cloud_integrations",
  "environments",
  "projects",
  "resource_types",
  "materializations",
  "databases",
  "schemas",
  // "is_stale", // should be here, but commented out because its feature flagged
];

const getDefaultFilter = (): Record<string, string[]> => {
  const defaultFilter: Record<string, string[]> = {};
  FILTER_KEYS.forEach(k => (defaultFilter[k] = []));
  return defaultFilter;
};

const NoSearchResults = ({ search, itemCount }: { search: string; itemCount: number }) => (
  <div className={styles.table_list}>
    <Card>
      <CardContent className="al-pt-4">
        <Stack justify="between" align="center">
          <div className="fs-5 text-muted">{itemCount} results</div>
        </Stack>
        <div className={styles.card}>
          <div className="fs-4 fw-semibold mb-2">
            No results found for : <span className="text-primary">{search}</span>
          </div>
          <div className="fs-5 mb-2">Search help</div>
          <ul className="text-muted mb-2">
            <li>Check your search for typos</li>
            <li>Use more specific search terms</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  </div>
);

const DbtModels = () => {
  useBreadcrumb("dbt Models");

  const { staleResourcesEnabled } = useAppState();
  if (staleResourcesEnabled && !FILTER_KEYS.includes("is_stale")) {
    FILTER_KEYS.push("is_stale");
  }
  const [search, setSearch] = useState("");
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const navigate = useNavigate();

  const [schemaTable, setSchemaTable] = useState<DbtModel | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);
  const [errorForRk, setErrorForRk] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);

  const filter = useMemo((): Record<string, string[]> => {
    if (!searchParams.size) return getDefaultFilter();
    return FILTER_KEYS.reduce((acc: Record<string, string[]>, key) => {
      // Use the URL key mapping for integration filters
      const urlKey = FILTER_URL_KEY[key] || key;
      const params = searchParams.getAll(urlKey);
      if (params.length) {
        acc[key] = params;
      }
      return acc;
    }, {});
  }, [searchParams]);

  const { data: dbtModelsFilters } = useQuery({
    queryKey: [GET_DBT_MODELS_FILTERS],
    queryFn: async (): Promise<ComputedFilters> => {
      const dbtModelsFilters = await getDbtModelsFilters();
      if (!dbtModelsFilters) return {};

      // Split dbt_integrations into dbt_core and dbt_cloud based on integration_type
      const dbtCoreIntegrations = dbtModelsFilters.dbt_integrations.filter(
        i => i.integration_type === "dbt_core"
      );
      const dbtCloudIntegrations = dbtModelsFilters.dbt_integrations.filter(
        i => i.integration_type === "dbt_cloud"
      );

      const envGroups: Record<string, string[]> = {};
      dbtModelsFilters.environments.forEach((e: FilterOption) => {
        envGroups[e.label] = envGroups[e.label] || [];
        envGroups[e.label].push(e.value);
      });
      const environments: FilterOptionWithValue[] = Object.entries(envGroups).map(([k, v]) => ({
        label: k,
        value: k,
        _value: v,
      }));

      // Destructure to exclude integrations_with_environments (not a FilterOption array)
      const { integrations_with_environments, ...dbtModelsFiltersRest } = dbtModelsFilters;
      const result: ComputedFilters = {
        ...dbtModelsFiltersRest,
        environments,
        projects: disambiguateProjects(dbtModelsFilters.projects, integrations_with_environments),
        dbt_core_integrations: dbtCoreIntegrations,
        dbt_cloud_integrations: dbtCloudIntegrations,
      };

      // Add resource status options
      if (staleResourcesEnabled) {
        result.is_stale = [
          { label: "Active", value: "false" },
          { label: "Stale", value: "true" },
        ];
      }
      return result;
    },
  });

  // Build a map from integration_id -> connection_name for dbt_cloud integrations
  const connectionNameMap = useMemo((): Record<string, string> => {
    const cloudIntegrations = dbtModelsFilters?.dbt_cloud_integrations;
    if (!cloudIntegrations) return {};
    return (cloudIntegrations as IntegrationFilterOption[]).reduce(
      (acc: Record<string, string>, integration: IntegrationFilterOption) => {
        if (integration.connection_name) {
          acc[integration.value] = integration.connection_name;
        }
        return acc;
      },
      {}
    );
  }, [dbtModelsFilters?.dbt_cloud_integrations]);

  // Initialize page and pageSize from URL params for state persistence
  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);

  const setPage = (newPage: number) => {
    updateSearchParamsByKey({ page: [newPage.toString()] });
  };

  const handlePageSizeChange = (newSize: number) => {
    updateSearchParamsByKey({ pageSize: [newSize.toString()], page: ["0"] });
  };

  const getTableParams = (): GetDbtModelsParams => {
    const filters: DbtModelFilterAccumulator = {};
    for (const k of FILTER_KEYS) {
      if (!filter[k]?.length) continue;
      const beKey = FILTER_BE_KEYS[k];
      if (k === "environments" && dbtModelsFilters) {
        const envOptions = dbtModelsFilters[k] as FilterOptionWithValue[];
        filters[beKey] = envOptions
          .filter(item => filter[k].includes(item.value))
          .flatMap(item => item._value || []);
      } else {
        // Combine values if the backend key already exists (dbt_core_integrations and dbt_cloud_integrations share the same BE key)
        const existing = filters[beKey];
        filters[beKey] = existing ? [...existing, ...filter[k]] : filter[k];
      }
    }
    return {
      page: page + 1,
      size: pageSize,
      ...(search ? { search } : {}),
      ...filters,
    };
  };

  const { data, isLoading } = useQuery({
    queryKey: [GET_DBT_MODELS, page, pageSize, search, ...FILTER_KEYS.flatMap(k => filter[k])],
    queryFn: () => getDbtModels(getTableParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      // Adjust page if current page exceeds available pages (but only if there are pages)
      if ((data.pages ?? 0) > 0 && page >= (data.pages ?? 0)) {
        setPage(Math.max(0, (data.pages ?? 0) - 1));
      }
      if (data?.total) setFilterApplied(true);
    }
  }, [data, page]);

  const { mutate: fetchdbtModel } = useMutation({
    mutationFn: getDbtModelByRk,
    onSuccess: data => {
      if (!data) return;
      navigate(`/dbt_models/${btoa(data.rk)}`, {
        state: { from: "dbt_models" },
      });
      setErrorForRk("");
    },
    onError: (error: Error) => {
      setModelError(error?.message || "Failed to load model details");
      setTimeout(() => {
        setModelError(null);
        setErrorForRk("");
      }, 5000);
    },
  });

  const handleDetailsClick = (table: DbtModel) => {
    if (table?.rk) {
      fetchdbtModel({ dbt_model_rk: table?.rk });
      setErrorForRk(table?.rk);
    }
  };

  if (!isLoading && !filterApplied && (!data || !data?.total)) {
    return <NoDbtModels />;
  }

  return (
    <>
      <div>
        <Stack className="mb-4" align="center" justify="between">
          <Stack align="center">
            <Typography variant="page-heading">dbt Models</Typography>
            <Tooltip
              title={
                <Stack direction="column" gap="sm">
                  <span>
                    If column details are not displaying for some models, verify that the
                    catalog.json file was included in your dbt artifacts upload.
                  </span>
                  <span>
                    Re-upload your connection artifacts with the catalog file to restore
                    column-level visibility.
                  </span>
                </Stack>
              }
            >
              <IconInfoCircle size={18} className="al-text-muted-foreground al-cursor-help" />
            </Tooltip>
          </Stack>
        </Stack>
        <Card>
          <CardHeader>
            <Stack gap={"lg"} justify={"between"} className={"al-w-full"}>
              <DbtModelsNewFilters
                onSearchChange={value => {
                  setSearch(value);
                  updateSearchParamsByKey({ page: ["0"] });
                }}
              />
            </Stack>
          </CardHeader>
          <CardContent>
            <Stack>
              {isLoading ? (
                <Stack justify={"center"} align={"start"} className={"al-flex-grow"}>
                  <RelativeComponentLoader label="Loading dbt models..." />
                </Stack>
              ) : search && (data?.total ?? 0) === 0 ? (
                <NoSearchResults search={search} itemCount={data?.total ?? 0} />
              ) : (
                <TableList
                  tables={data?.items}
                  onItemClick={handleDetailsClick}
                  paginate={{
                    itemCount: data?.total ?? 0,
                    page: page,
                    pageSize: pageSize,
                    numPages: data?.pages ?? 0,
                    onPageClick: setPage,
                    showPageSize: true,
                    onPageSizeChange: handlePageSizeChange,
                  }}
                  modelError={modelError}
                  errorForRk={errorForRk}
                  onDetailsClick={handleDetailsClick}
                  onViewSchema={setSchemaTable}
                  connectionNameMap={connectionNameMap}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </div>
      <Sheet open={!!schemaTable} onOpenChange={open => !open && setSchemaTable(null)}>
        <SheetContent side="right" className="!al-w-[446px]" aria-label="Dbt model dialog">
          <ColumnsModal table={schemaTable} onDetailsClick={handleDetailsClick} />
        </SheetContent>
      </Sheet>
    </>
  );
};

export { DbtModels };
